import { JwtPayload, User, ParsedClientGroup} from './models';



export const CLIENT_GROUP_PREFIX = '/Clients/';   // Our clients group
export const ANEP_GROUP = '/Anep';    // Our staff group
export const ROSTER_SUBGROUP = 'members'; // The "roster" subgroup of a client group, for staff attached to that client


export function buildUserFromPayload(payload: JwtPayload, clientId: string,): User {
  const clientRoles = payload.resource_access?.[clientId]?.roles || [];
  const realmRoles = payload.realm_access?.roles || [];
  const groups: string[] = Array.isArray(payload.groups) ? payload.groups : [];
  const clientIds = clientIdsFromGroups(groups.filter((g) => typeof g === 'string'), environmentFromOAuthClientId(clientId));
  const preferredUsername = typeof payload.preferred_username === 'string' ? payload.preferred_username : '';
  return {
    id: typeof payload.sub === 'string' ? payload.sub : '',
    email: typeof payload.email === 'string' ? payload.email : '',
    name: typeof payload.name === 'string' ? payload.name : preferredUsername,
    preferredUsername,
    roles: [...new Set([...clientRoles, ...realmRoles])],
    clientIds,
    isAnepStaff: isAnepStaffFromGroups(groups, environmentFromOAuthClientId(clientId)),
  };
};

export const environmentFromOAuthClientId = (oauthClientId: string): string => {
  if (!oauthClientId || !oauthClientId.includes('-')) return 'dev';
  return oauthClientId.slice(oauthClientId.lastIndexOf('-') + 1).toLowerCase();
};
// pour recupérer l'environnement à partir du clientId OAuth, (dev, staging, prod) pour savoir si l'utilisateur est un membre du personnel ANEP ou un client.
// Et isAnepStaffFromGroups pour savoir si l'utilisateur est un membre du personnel ANEP ou un client, en fonction de ses groupes et de l'environnement.
// exemple pour groupes : /Anep/dev, /Clients/12345/dev, /Clients/12345/members/dev, /Clients/12345/prod, /Clients/12345/members/prod
// retunr true si l'utilisateur est un membre du personnel ANEP, false sinon.
export const isAnepStaffFromGroups = (groupPaths: string[], env: string): boolean => {
  const target = (env || '').toLowerCase();
  let flat = false;
  const envs = new Set<string>();

  for (const g of groupPaths || []) {
    if (typeof g !== 'string') continue;
    if (g === ANEP_GROUP) {
      flat = true;
    } else if (g.startsWith(`${ANEP_GROUP}/`)) {
      const remainder = g.slice(ANEP_GROUP.length + 1).replace(/^\/+|\/+$/g, '');
      if (remainder && !remainder.includes('/')) envs.add(remainder.toLowerCase());
    }
  }

  // Scoped member: the subgroups are authoritative.
  if (envs.size > 0) return envs.has(target);
  return flat;
};
// Pour clientIdsFromGroups, il s'agit de récupérer les identifiants des clients auxquels l'utilisateur a accès, 
// en fonction de ses groupes et de l'environnement. Les groupes peuvent être sous forme de /Clients/{clientId}/{env} ou /Clients/{clientId}/members/{env}. 
// Si l'utilisateur est un membre du personnel ANEP, il a accès à tous les clients, sinon il n'a accès qu'aux clients pour lesquels il est membre.
// exemple pour groupes : /Anep/dev, /Clients/12345/dev, /Clients/12345/members/dev, /Clients/12345/prod, /Clients/12345/members/prod et pour l'environnement dev, il aura accès au client 12345, mais pas pour l'environnement prod.
// donc les membres du personnel ANEP sur dev auront accès seulement aux clinets 

// return de ces  /Anep/dev, /Clients/12345/dev, /Clients/12345/members/dev, /Clients/12345/prod, /Clients/12345/members/prod au final pour l'environnement dev : ['12345'] et pour l'environnement prod : ['12345'].
export const clientIdsFromGroups = (groupPaths: string[], env: string): string[] => {
  const target = (env || '').toLowerCase();
  const order: string[] = [];
  const flat = new Set<string>();
  const subgroups = new Map<string, Set<string>>();

  for (const path of groupPaths || []) {
    const parsed = parseClientGroup(path);
    if (!parsed) continue;

    if (!order.includes(parsed.clientId)) order.push(parsed.clientId);

    if (parsed.subgroup === null) {
      flat.add(parsed.clientId);
    } else {
      const set = subgroups.get(parsed.clientId) ?? new Set<string>();
      set.add(parsed.subgroup);
      subgroups.set(parsed.clientId, set);
    }
  }

  return order.filter((clientId) => {
    const subs = subgroups.get(clientId);
    // Migrated user: environment subgroups are authoritative.
    if (subs && subs.size > 0) return subs.has(target);
    // Legacy user: the flat group still grants every environment.
    return flat.has(clientId);
  });
};


// le retour de decodeJwtPayload est un objet JwtPayload qui contient les informations du token JWT décodé, ou null si le token est invalide. exemple de retour : { sub: '1234567890', email: 'user@example.com' }
// donx exemple de parsesd token : eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwiZW1haWwiOiJ1c2VyQGV4YW1wbGUuY29tIiwibmFtZSI6IkpvaG4gRG9lIiwicHJlZmVycmVkX3VzZXJuYW1lIjoiamRvZSIsImV4cCI6MTYwOTQ5MjAwMCwiaWF0IjoxNjA5NDg4NDAwLCJncm91cHMiOlsiL0NsaWVudHMvMTIzNDU2L2RldiIsIi9DbGllbnRzLzEyMzQ1Ni9tZW1iZXJzL2RldiIsIi9BbmVwL2RldiJdLCJyZWFsbV9hY2Nlc3MiOnsicm9sZXMiOlsiU3VwZXJBZG1pbiIsIkFkbWluIl19LCJyZXNvdXJjZV9hY2Nlc3MiOnsicHJvdmlzaW9uaW5nIjpbInJlYWRfYWxsIiwid3JpdGVfYWxsIl19fQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c
// le retour de decodeJwtPayload pour ce token serait :
// {
//  "sub": "123456789",
//  "preferred_username": "youness",
//  "name": "Youness Chaanani",
//  "email": "youness@example.com",
//  "isAnepStaff": true,
//  "isSuperAdmin": false,
//  "roles": [
//    "admin",
//    "user"
//  ],
//  "exp": 1799849600,
//  "iat": 1799846000
//   } 
  

export function decodeJwtPayload(token: string | null | undefined): JwtPayload | null {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    const json = atob(padded);
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as JwtPayload;
  } catch {
    return null;
  }
}



// parseClientGroup permet de parser un groupe de client et de retourner un objet ParsedClientGroup contenant l'identifiant du client et le sous-groupe (si présent).
// par exemple, pour le groupe /Clients/12345/dev, il retournera { clientId: '12345', subgroup: null }, et pour le groupe /Clients/12345/members/dev, il retournera { clientId: '12345', subgroup: 'members' }.
// un autre exemple, pour le groupe /Clients/12345/members/dev, il retournera { clientId: '12345', subgroup: 'members' }.

export const parseClientGroup = (groupPath: string): ParsedClientGroup | null => {
  if (typeof groupPath !== 'string' || !groupPath.startsWith(CLIENT_GROUP_PREFIX)) {
    return null;
  }

  const remainder = groupPath.slice(CLIENT_GROUP_PREFIX.length).replace(/^\/+|\/+$/g, '');
  if (!remainder) return null;

  const parts = remainder.split('/');
  if (parts.length === 1) return { clientId: parts[0], subgroup: null };
  if (parts.length === 2) return { clientId: parts[0], subgroup: parts[1].toLowerCase() };
  return null;
};





